import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { HiCalendar, HiFlag, HiChatAlt, HiUserCircle } from 'react-icons/hi'

const PRIORITY_COLORS = {
  low: 'priority-low',
  medium: 'priority-medium',
  high: 'priority-high',
  critical: 'priority-critical',
}

const PRIORITY_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

export default function TaskCard({ task, isDragging = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: task.id,
    data: {
      type: 'task',
      task,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
    touchAction: 'none',
  }

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done'

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        card p-3 cursor-grab active:cursor-grabbing group
        transition-all duration-200
        hover:shadow-md hover:border-surface-300 dark:hover:border-surface-600 hover:-translate-y-0.5
        ${isDragging ? 'shadow-lg ring-2 ring-brand-500' : ''}
        ${isSortableDragging ? 'opacity-50' : ''}
      `}
    >
      {/* Priority Bar */}
      {task.priority && (
        <div className={`${PRIORITY_COLORS[task.priority]} h-1 -mx-3 -mt-3 mb-3 rounded-t-xl`}></div>
      )}

      {/* Title */}
      <Link
        to={`/tasks/${task.id}`}
        className="block font-medium text-surface-800 dark:text-surface-200 group-hover:text-brand-600 transition-colors line-clamp-2"
        onClick={(e) => e.stopPropagation()}
      >
        {task.title}
      </Link>

      {/* Description Preview */}
      {task.description && (
        <p className="text-sm text-surface-500 dark:text-surface-400 mt-1 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Tags */}
      {task.tags && task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {task.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="text-xs px-2 py-0.5 bg-surface-100 dark:bg-surface-700 text-surface-600 dark:text-surface-400 rounded-full"
            >
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="text-xs text-surface-400 dark:text-surface-500">+{task.tags.length - 3}</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-surface-100 dark:border-surface-700">
        <div className="flex items-center gap-2">
          {/* Due Date */}
          {task.due_date && (
            <span className={`flex items-center gap-1 text-xs ${
              isOverdue ? 'text-red-600' : 'text-surface-500 dark:text-surface-400'
            }`}>
              <HiCalendar className="w-3 h-3" />
              {format(new Date(task.due_date), 'MMM d')}
            </span>
          )}

          {/* Priority Badge */}
          {task.priority && task.priority !== 'medium' && (
            <span className={`flex items-center gap-1 text-xs ${
              task.priority === 'critical' ? 'text-red-600' :
              task.priority === 'high' ? 'text-orange-600' :
              'text-surface-500 dark:text-surface-400'
            }`}>
              <HiFlag className="w-3 h-3" />
            </span>
          )}

          {/* Comment Count */}
          {task.comment_count > 0 && (
            <span className="flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400">
              <HiChatAlt className="w-3 h-3" />
              {task.comment_count}
            </span>
          )}
        </div>

        {/* Assignee */}
        {task.assignee ? (
          <div className="flex items-center gap-1" title={task.assignee.display_name}>
            <div className="w-6 h-6 bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 rounded-full flex items-center justify-center text-xs font-medium">
              {task.assignee.display_name?.charAt(0) || 'U'}
            </div>
          </div>
        ) : (
          <HiUserCircle className="w-6 h-6 text-surface-300 dark:text-surface-600" title="Unassigned" />
        )}
      </div>
    </div>
  )
}
